/**
 * Dashboard 仪表盘页面
 * 
 * 重构后结构:
 * - Dashboard/constants.tsx: 常量和配置
 * - Dashboard/AmazonNewsCard.tsx: 新闻卡片组件
 */

import React, { useEffect, useState } from 'react';
import { Card, Row, Col, Typography, Button, List, Avatar, Space, Spin, Empty } from 'antd';
import { RightOutlined, FileTextOutlined, BellOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from '../components/Layout/PageHeader';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/zh-cn';
// 子组件和常量
import { AmazonNewsCard } from './Dashboard/AmazonNewsCard';
import { WorldClockCard } from './Dashboard/WorldClockCard';
import { ExchangeRateCard } from './Dashboard/ExchangeRateCard';
import { CommunityNewsCard } from './Dashboard/CommunityNewsCard';
import { FreightIndexCard } from './Dashboard/FreightIndexCard';
import { HomeOutlined } from './Dashboard/constants';
import { dashboardService, AmazonNewsItem, CommunityNewsItem } from '../services/dashboardService';

import './Dashboard.css';
import './Dashboard/Widgets.css';

dayjs.extend(relativeTime);
dayjs.locale('zh-cn');

const { Text } = Typography;

const Dashboard: React.FC = () => {
    const navigate = useNavigate();
    const [newsLoading, setNewsLoading] = useState(true);
    const [news, setNews] = useState<AmazonNewsItem[]>([]);
    const [newsRegion, setNewsRegion] = useState<'all' | 'cn' | 'us'>('all');
    const [newsUpdatedAt, setNewsUpdatedAt] = useState<string>('');

    // 社区热帖状态
    const [communityNews, setCommunityNews] = useState<CommunityNewsItem[]>([]);
    const [communityNewsLoading, setCommunityNewsLoading] = useState(true);

    // 加载亚马逊新闻
    const fetchNews = async (region: 'all' | 'cn' | 'us' = newsRegion) => {
        setNewsLoading(true);
        try {
            const response = await dashboardService.getAmazonNews(region);
            setNews(response.data);
            setNewsUpdatedAt(response.updated_at);
        } catch (error) {
            console.error('Failed to fetch Amazon news:', error);
            setNews([]);
        } finally {
            setNewsLoading(false);
        }
    };

    // 加载社区热帖
    const fetchCommunityNews = async () => {
        setCommunityNewsLoading(true);
        try {
            const response = await dashboardService.getCommunityNews();
            // 确保 data 是数组
            const newsData = Array.isArray(response.data) ? response.data : [];
            setCommunityNews(newsData);
        } catch (error) {
            console.error('Failed to fetch Community news:', error);
            setCommunityNews([]);
        } finally {
            setCommunityNewsLoading(false);
        }
    };

    useEffect(() => {
        fetchNews(newsRegion);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [newsRegion]);

    useEffect(() => {
        fetchCommunityNews();
    }, []);

    return (
        <div className="dashboard-container">
            <PageHeader
                title="仪表盘"
                subtitle="查看全球动态与社区热点"
                icon={<HomeOutlined />}
            />

            <div className="dashboard-content">
                {/* 第一行：世界时钟 + 汇率看板 + 运价指数 */}
                <Row gutter={[20, 20]}>
                    <Col xs={24} xl={10}>
                        <WorldClockCard />
                    </Col>
                    <Col xs={24} xl={6}>
                        <ExchangeRateCard />
                    </Col>
                    <Col xs={24} xl={8}>
                        <FreightIndexCard />
                    </Col>
                </Row>

                {/* 第二行：亚马逊公告 + 社区热帖 (左右分栏) */}
                <Row gutter={[20, 20]} style={{ marginTop: 20 }}>
                    <Col xs={24} xl={12}>
                        <AmazonNewsCard
                            news={news}
                            newsRegion={newsRegion}
                            onRegionChange={setNewsRegion}
                            onRefresh={() => fetchNews()}
                            loading={newsLoading}
                            updatedAt={newsUpdatedAt}
                        />
                    </Col>
                    <Col xs={24} xl={12}>
                        <CommunityNewsCard
                            news={communityNews}
                            loading={communityNewsLoading}
                            onRefresh={fetchCommunityNews}
                        />
                    </Col>
                </Row>
            </div>
        </div>
    );
};

export default Dashboard;
